import * as React from 'react';
import { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import useProduction from '@/src/Production/components/hooks/useProduction';
import { fullName } from './shared';

/* Settings — team ICS feed (one-tap subscribe per client), active feed
   tokens, and integration status. */

const toWebcal = (url: string) => url.replace(/^https?/, 'webcal');
const toGoogleCal = (url: string) =>
    `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(toWebcal(url))}`;

export default function SettingsV2() {
    const {
        getTeamCalendarFeed,
        getCalendarFeeds,
        revokeCalendarFeed,
        getIntegrationsStatus,
    } = useProduction();
    const [teamFeed, setTeamFeed] = useState<any | null>(null);
    const [feeds, setFeeds] = useState<any[]>([]);
    const [status, setStatus] = useState<any | null>(null);
    const [copied, setCopied] = useState(false);

    const reloadFeeds = async () => {
        setFeeds((await getCalendarFeeds().catch(() => [])) ?? []);
    };

    useEffect(() => {
        let mounted = true;
        (async () => {
            const [team, list, integrations] = await Promise.all([
                getTeamCalendarFeed().catch(() => null),
                getCalendarFeeds().catch(() => []),
                getIntegrationsStatus().catch(() => null),
            ]);
            if (!mounted) return;
            setTeamFeed(team);
            setFeeds(list ?? []);
            setStatus(integrations);
        })();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const copyLink = async () => {
        if (!teamFeed?.url) return;
        await navigator.clipboard.writeText(teamFeed.url).catch(() => null);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <main className="mx-auto max-w-[1060px] px-[clamp(18px,3vw,34px)] pt-4 pb-8">
            <div className="ps-label mb-3.5">Jobs · Settings</div>

            <section className="mb-9 max-w-[440px]">
                <div className="ps-label mb-2.5">Team calendar</div>
                <div className="rounded-2xl border border-dashed border-border p-4">
                    {!teamFeed && (
                        <div className="text-[12.5px] text-faint">Loading…</div>
                    )}
                    {teamFeed && (
                        <>
                            <a
                                href={toWebcal(teamFeed.url)}
                                className="flex h-10 items-center justify-center rounded-full bg-foreground text-[13px] font-semibold text-background no-underline"
                            >
                                Add to iCal / Outlook
                            </a>
                            <a
                                href={toGoogleCal(teamFeed.url)}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-2.5 flex h-10 items-center justify-center rounded-full border border-border text-[13px] font-semibold text-foreground no-underline"
                            >
                                Add to Google Calendar
                            </a>
                            <button
                                type="button"
                                onClick={copyLink}
                                className="mt-3 block w-full cursor-pointer border-0 bg-transparent text-center font-[inherit] text-[12px] font-semibold text-faint hover:text-brand-ink"
                            >
                                {copied ? 'Copied' : 'or copy the feed link'}
                            </button>
                            <p className="mb-0 mt-2.5 text-center text-[11px] leading-relaxed text-faint">
                                Every production (and pending pipeline deals)
                                syncs to your calendar automatically.
                            </p>
                        </>
                    )}
                </div>
            </section>

            <section className="mb-9 max-w-[560px]">
                <div className="ps-label mb-1.5">Active feeds</div>
                {feeds.length === 0 && (
                    <div className="text-[12.5px] text-faint">None yet.</div>
                )}
                {feeds.map((f) => (
                    <div
                        key={f.id}
                        className="flex items-center gap-3 border-t border-line py-[9px] text-[12.5px] first:border-t-0"
                    >
                        <span className="w-[150px] flex-none font-semibold">
                            {f.subjectType === 'team'
                                ? 'Team feed'
                                : fullName({
                                      firstName: f.firstName,
                                      lastName: f.lastName,
                                  }) || 'Staff feed'}
                        </span>
                        <span className="min-w-0 flex-1 truncate font-mono text-[10.5px] text-faint">
                            …{String(f.token).slice(-10)}.ics
                        </span>
                        <span className="flex-none text-[11px] text-faint">
                            since {moment(f.createdAt).format('MMM D')}
                        </span>
                        <button
                            type="button"
                            onClick={async () => {
                                if (
                                    !window.confirm(
                                        'Revoke this feed? Every calendar subscribed to it breaks immediately.',
                                    )
                                )
                                    return;
                                await revokeCalendarFeed(+f.id).catch(
                                    () => null,
                                );
                                await reloadFeeds();
                            }}
                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11.5px] font-semibold text-attention"
                        >
                            Revoke
                        </button>
                    </div>
                ))}
                <p className="mt-2 text-[11px] text-faint">
                    Revoking a feed breaks that subscription everywhere it was
                    added; a new link is issued on next use.
                </p>
            </section>

            <section className="max-w-[560px]">
                <div className="ps-label mb-1.5">Integrations</div>
                <div className="flex items-center gap-3 border-t border-line py-[9px] text-[12.5px]">
                    <span className="w-[150px] flex-none font-semibold">
                        Attio
                    </span>
                    <span
                        className={
                            'flex-none text-[11.5px] font-semibold ' +
                            (status?.attioConfigured
                                ? 'text-good'
                                : 'text-attention')
                        }
                    >
                        {status?.attioConfigured
                            ? 'Connected'
                            : 'Not configured'}
                    </span>
                    <span className="min-w-0 flex-1 truncate font-mono text-[10.5px] text-faint">
                        POST {status?.webhookPath ?? ''}
                    </span>
                </div>
                <div className="flex items-center gap-3 border-t border-line py-[9px] text-[12.5px]">
                    <span className="w-[150px] flex-none font-semibold">
                        Google Maps
                    </span>
                    <span
                        className={
                            'flex-none text-[11.5px] font-semibold ' +
                            (status?.googleMapsConfigured
                                ? 'text-good'
                                : 'text-attention')
                        }
                    >
                        {status?.googleMapsConfigured
                            ? 'Connected'
                            : 'Not configured'}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[11px] text-faint">
                        timezone lookups; Places autocomplete needs a browser
                        key
                    </span>
                </div>
            </section>
        </main>
    );
}
